/**
 * Formularul de invitare (email + rol) și linkul rezultat. Folosit atât în
 * Cont, cât și în pop-up-ul deschis din lista de spații.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { errorMessage } from '@/api/client';
import { invitesApi, type InviteCreateResponse, type InviteRole } from '@/api/invites';

export default function InviteForm({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<InviteRole>('member');
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<InviteCreateResponse | null>(null);
  const [copied, setCopied] = useState(false);

  const createMutation = useMutation({
    mutationFn: () => invitesApi.create(tenantId, { email: email.trim(), role }),
    onSuccess: (resp) => {
      setError(null);
      setEmail('');
      setCopied(false);
      setCreated(resp);
      queryClient.invalidateQueries({ queryKey: ['invites', tenantId] });
    },
    onError: (err) => {
      setError(errorMessage(err, t('common.error')));
    },
  });

  const copyUrl = async (path: string) => {
    const url = `${window.location.origin}${path}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
  };

  return (
    <div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!email.trim()) return;
          createMutation.mutate();
        }}
        className="flex flex-wrap gap-2 mb-4"
      >
        <input
          type="email"
          className="input flex-1 min-w-[12rem]"
          aria-label={t('tenant_invites.email_label')}
          placeholder={t('tenant_invites.invite_email_placeholder')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={createMutation.isPending}
        />
        <select
          className="input w-32"
          aria-label={t('tenant_invites.role_label')}
          value={role}
          onChange={(e) => setRole(e.target.value as InviteRole)}
          disabled={createMutation.isPending}
        >
          <option value="admin">admin</option>
          <option value="member">member</option>
          <option value="viewer">viewer</option>
        </select>
        <button type="submit" className="btn-primary text-sm" disabled={createMutation.isPending}>
          {createMutation.isPending ? t('tenant_invites.creating') : t('tenant_invites.invite')}
        </button>
      </form>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded px-3 py-2 mb-3">
          {error}
        </div>
      )}

      {created && (
        <div className="border border-green-300 dark:border-green-800 bg-green-50 dark:bg-green-900/30 rounded-md p-3 mb-4">
          <p className="text-sm text-green-800 dark:text-green-300 font-medium mb-2">
            {t('tenant_invites.created_for', { email: created.email })}
          </p>
          {created.email_sent && (
            <p className="text-xs text-green-800 dark:text-green-300 mb-2" data-testid="invite-email-sent">
              {t('tenant_invites.email_sent', { email: created.email })}
            </p>
          )}
          <div className="flex items-center gap-2">
            <code className="text-xs font-mono bg-[var(--bg-elevated)] border border-green-200 dark:border-green-800 rounded px-2 py-1 flex-1 truncate">
              {window.location.origin}
              {created.accept_url}
            </code>
            <button
              type="button"
              className="btn-outline text-xs"
              onClick={() => copyUrl(created.accept_url)}
            >
              {copied ? t('common.copied') : t('common.copy')}
            </button>
          </div>
          <p className="text-[10px] text-[var(--text-dim)] mt-2">
            {t('tenant_invites.token_one_time')}
          </p>
        </div>
      )}
    </div>
  );
}
